import { startOfDayBrt } from '@empire/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Avatar } from '../../components/Avatar'
import { FilterBar, FilterSelect } from '../../components/filters'
import { IconClock } from '../../components/icons'
import { avatarColor, fullName, initials } from '../../lib/format'
import { lerIdioma, t } from '../../lib/i18n'
import { stageColors } from '../../lib/palette'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/agendados')({ component: Scheduled })

/** Sob demanda e guardado por idioma — como constante ficaria preso em pt-BR. */
const QUANDO = new Map<string, Intl.DateTimeFormat>()

function quando(valor: Date | string | number): string {
  const idioma = lerIdioma()
  let formatador = QUANDO.get(idioma)
  if (!formatador) {
    formatador = new Intl.DateTimeFormat(idioma, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    QUANDO.set(idioma, formatador)
  }
  return formatador.format(new Date(valor))
}

/**
 * Prazos, em dia-calendário de Brasília — a mesma regra do relatório, vinda de
 * `@empire/shared`. "Hoje" aqui é o que ainda sai antes da virada do dia.
 */
/** Rótulos em português; traduzidos na hora de montar as opções. */
const PRAZOS = [
  { key: 'hoje' as const, label: 'Hoje', dias: 1 },
  { key: 'amanha' as const, label: 'Até amanhã', dias: 2 },
  { key: 'semana' as const, label: 'Próximos 7 dias', dias: 7 },
]

type PrazoKey = (typeof PRAZOS)[number]['key']

function Scheduled() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const upcoming = useQuery(trpc.conversation.upcoming.queryOptions())

  const [prazo, setPrazo] = useState<PrazoKey | null>(null)
  const [conta, setConta] = useState<string | null>(null)

  const cancel = useMutation({
    ...trpc.conversation.cancelSchedule.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [['conversation']] }),
  })

  const todos = upcoming.data ?? []
  const contas = [...new Set(todos.map((item) => item.channelName))].sort()

  const items = useMemo(() => {
    let rows = todos
    if (conta) rows = rows.filter((item) => item.channelName === conta)

    if (prazo) {
      // Limite é a meia-noite de Brasília do dia seguinte ao último incluído.
      const dias = PRAZOS.find((opcao) => opcao.key === prazo)?.dias ?? 1
      const limite = startOfDayBrt(Date.now()).getTime() + dias * 86_400_000
      rows = rows.filter((item) => new Date(item.sendAt).getTime() < limite)
    }

    return rows
  }, [todos, conta, prazo])

  const ativos = (prazo ? 1 : 0) + (conta ? 1 : 0)

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        <h1 className="font-display text-2xl">{t('Agendados')}</h1>
        <p className="mt-1 text-xs leading-relaxed text-ink-2">
          {t(
            'Tudo que ainda vai sair, de todas as contas. O CRM confere de minuto em minuto, então o envio pode atrasar até um minuto do horário marcado.',
          )}
        </p>

        {todos.length > 0 && (
          <FilterBar
            active={ativos}
            onClear={() => {
              setPrazo(null)
              setConta(null)
            }}
          >
            <FilterSelect
              label={t('Prazo')}
              placeholder={t('Tudo que vem')}
              options={PRAZOS.map((opcao) => ({ value: opcao.key, label: t(opcao.label) }))}
              values={prazo ? [prazo] : []}
              onChange={([valor]) => setPrazo((valor as PrazoKey) ?? null)}
            />

            <FilterSelect
              label={t('Conta')}
              placeholder={t('Todas')}
              options={contas.map((nome) => ({ value: nome, label: nome }))}
              values={conta ? [conta] : []}
              onChange={([valor]) => setConta(valor ?? null)}
            />
          </FilterBar>
        )}

        {upcoming.isPending && <p className="mt-6 text-xs text-ink-3">{t('Carregando…')}</p>}

        {!upcoming.isPending && items.length === 0 && (
          <div className="mt-6 rounded-lg border border-dashed border-line-strong px-4 py-10 text-center">
            <IconClock className="mx-auto size-6 text-ink-3" />
            <p className="mt-2 text-sm text-ink-2">
              {ativos > 0 ? t('Nada agendado nesse recorte.') : t('Nenhuma mensagem agendada.')}
            </p>
            {ativos === 0 && (
              <p className="mt-1 text-2xs text-ink-3">
                {t('Use o relógio no campo de mensagem para agendar um envio.')}
              </p>
            )}
          </div>
        )}

        <ul className="mt-6 flex flex-col gap-2">
          {items.map((item) => {
            const lead = {
              firstName: item.leadFirstName,
              lastName: item.leadLastName,
              username: item.leadUsername,
            }
            const name = fullName(lead)
            const text = typeof item.payload?.text === 'string' ? item.payload.text : ''

            return (
              <li
                key={item.id}
                className="flex items-start gap-3 rounded-lg border border-line bg-surface p-3"
              >
                <Avatar
                  leadId={item.leadId}
                  name={name}
                  hasPhoto={item.hasPhoto}
                  className="size-9 flex-none rounded-full text-xs"
                />

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-2">
                    <Link
                      to="/inbox"
                      search={{ c: item.conversationId }}
                      className="text-sm font-semibold hover:underline"
                    >
                      {name}
                    </Link>
                    {item.leadUsername && (
                      <span className="text-2xs text-ink-3">@{item.leadUsername}</span>
                    )}
                    <span className="text-2xs text-ink-3">{item.channelName}</span>
                    <span className="ml-auto text-2xs font-semibold text-accent-ink">
                      {quando(item.sendAt)}
                    </span>
                  </span>

                  {/* Etapa, responsável e tags: a pergunta de quem abre esta
                      tela é "ainda faz sentido mandar isso?", e ela não se
                      responde só com o texto agendado. */}
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    {item.stageLabel && (
                      <span className="inline-flex items-center gap-1 text-2xs text-ink-2">
                        <span
                          className="size-2 rounded-[2px]"
                          style={{ background: stageColors(item.stageKey).solid }}
                        />
                        {t(item.stageLabel)}
                      </span>
                    )}

                    {item.assigneeName && (
                      <span className="inline-flex items-center gap-1 text-2xs text-ink-2">
                        <span
                          className="grid size-[15px] place-items-center rounded-full text-[8px] font-bold text-white"
                          style={{ background: avatarColor(item.assigneeName) }}
                        >
                          {initials(item.assigneeName)}
                        </span>
                        {item.assigneeName}
                      </span>
                    )}

                    {item.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center rounded border px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wider"
                        style={{
                          borderColor: `color-mix(in oklab, ${tag.color} 45%, transparent)`,
                          background: `color-mix(in oklab, ${tag.color} 12%, transparent)`,
                          color: tag.color,
                        }}
                      >
                        {tag.label}
                      </span>
                    ))}
                  </span>

                  <span className="mt-1 block text-xs text-ink-2">{text}</span>
                  {item.authorName && (
                    <span className="mt-1 block text-2xs text-ink-3">
                      {t('Agendada por {nome}', { nome: item.authorName })}
                    </span>
                  )}
                </span>

                <button
                  type="button"
                  onClick={() => cancel.mutate({ scheduledId: item.id })}
                  disabled={cancel.isPending}
                  className="flex-none text-2xs font-semibold text-danger hover:underline disabled:opacity-50"
                >
                  {t('Cancelar')}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
